import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { matchPath, useLocation, useNavigate, useParams } from "react-router-dom";
import { Layout } from "../../components";
import { useAppDispatch } from "../../redux";
import { useLazyGetSearchPromptByIdQuery } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { updateSearchPromptSlice } from "../../redux/slices/searchPrompts";
import { AppRoutes } from "../../router/routes";
import { BasicDetails } from "./basicDetails";
import { NavBars } from "./utils";

const ViewSearchPrompt: React.FC<{ children?: JSX.Element, currentPath: string, readOnly?: boolean }> = ({ currentPath, readOnly }) => {

    const params = useParams();
    const navigate = useNavigate();

    const location: any = useLocation();
    const paths = location.state?.paths ?? [];

    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [getSearchPromptById, searchPromptData] = useLazyGetSearchPromptByIdQuery();

    const searchPromptId = params?.id ?? "New";

    const isEdit = !['New', ':id'].includes(searchPromptId);

    const giveMeComponent = () => {
        // Basic Details
        if (matchPath(location.pathname, AppRoutes.viewSearchPromptBasicDetails(searchPromptId))) {
            return <BasicDetails isEdit={isEdit} searchId={searchPromptId} readOnly={readOnly} />
        }

        return <></>
    };

    React.useEffect(() => {
        if (searchPromptId === ':id') {
            dispatch(closeBackdrop());
            navigate(AppRoutes.searchPrompts)
        }
    });

    React.useEffect(() => {
        if (isEdit) {
            getSearchPromptById(searchPromptId);
        }
        // eslint-disable-next-line    
    }, [searchPromptId]);

    React.useEffect(() => {
        if (searchPromptData.isLoading) {
            dispatch(openBackdrop("Fetching Search Prompt Details..."));
        } else {
            dispatch(closeBackdrop());
        }
        // eslint-disable-next-line
    }, [searchPromptData.isLoading])

    React.useEffect(() => {
        const updateValue = async () => {
            if (isEdit && searchPromptData.isSuccess) {
                dispatch(updateSearchPromptSlice({
                    _id: searchPromptData?.data?._id ?? "",
                    version: searchPromptData?.data?.version ?? 0,
                    sequence_id: searchPromptData?.data?.sequence_id ?? "New",
                    basic_details: {
                        name: searchPromptData.data.name,
                        description: searchPromptData.data.description,
                        prompt_text: searchPromptData.data.prompt_text
                    },
                    error: {
                        basic_details: { name: false, description: false, prompt_text: false },
                    }
                }))
            }
        }
        updateValue();
        // eslint-disable-next-line
    }, [searchPromptData.isSuccess]);

    React.useEffect(() => {
        if (!searchPromptData.isFetching && searchPromptData.isError) {
            enqueueSnackbar('404 - Unable to find the requested Search Prompt', { variant: "error" });
            dispatch(openConfirmationDialog({
                title: "404 - Not Found",
                body: "Unable to find the requested Search Prompt",
                positiveBtn: "Ok",
                onOk: () => {
                    dispatch(closeConfirmationDialog())
                    navigate(AppRoutes.searchPrompts)
                },
                hideNegativeBtn: true
            }))
        }
        // eslint-disable-next-line
    }, [searchPromptData.isError])

    let history = [
        { label: "AI", onClick: () => navigate(AppRoutes.searchPrompts) },
        { label: "Search Prompts", onClick: () => navigate(AppRoutes.searchPrompts) },
        { label: isEdit ? `${searchPromptData.isLoading ? "Loading..." : `${searchPromptData?.data?.sequence_id ?? '...'}`}` : 'New', onClick: () => navigate(AppRoutes.viewSearchPromptBasicDetails(searchPromptId)) }
    ];

    if (paths?.length > 0) {
        history = [
            ...(paths ?? []),
            history[1],
            history[2]
        ]
    }

    return (
        <Box
            sx={{
                p: 0.5,
                height: "100%"
            }}>
            <Layout
                history={history}
                currentPath={currentPath}
                navBars={NavBars(searchPromptId, isEdit)}
                mainPanel={giveMeComponent()}
                sideNavVariant={"whiteboard"}
                hideNavBarByDefault={true}
                locationState={location?.state}
                resizable
            />
        </Box>
    );
}

export default ViewSearchPrompt;